import { connectSocket, disconnectSocket } from '@/Redux/Action/socketActions';
import {
  MOBILE_APP_ONLY_MESSAGE,
  canAccessWebPath,
  clearWebSession,
  extractAuthUser,
  hasWebAccess,
} from '@/auth/webAccess';
import { CommanServices } from '@/services/commanServices';
import { SaaSServices } from '@/services/saasServices';
import { isPlatformSuperadmin } from '@/auth/saasSession';
import {
  disabledFeatureForWebPath,
  labelForLicenseFeature,
} from '@/auth/featureEntitlements';
import { LOGOUT_TRANSITION_EVENT, readLogoutTransition } from '../auth/logoutTransition';
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import { Link, Navigate, useLocation } from 'react-router-dom'

const AccessNotice = ({ title, message, actionTo, actionLabel }) => (
  <main className="min-h-screen bg-slate-100 px-6 py-12 flex items-center justify-center">
    <section className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-xl" role="alert">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-2xl" aria-hidden="true">
        !
      </div>
      <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
      <p className="mt-3 text-sm leading-6 text-slate-600">{message}</p>
      <Link
        to={actionTo}
        className="mt-6 inline-flex min-h-10 items-center justify-center rounded-lg bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800"
      >
        {actionLabel}
      </Link>
    </section>
  </main>
);

const LogoutTransitionScreen = ({ isSaaS }) => (
  <main
    className={`flex min-h-screen items-center justify-center ${isSaaS ? 'dms-saas-shell bg-[#070a1c] text-white' : 'bg-slate-100 text-slate-900'}`}
    role="status"
    data-testid="logout-transition-screen"
    data-theme={isSaaS ? 'saas-dark' : 'tenant-light'}
  >
    <section className={`flex items-center gap-3 rounded-xl border px-5 py-4 shadow-xl ${isSaaS
      ? 'border-white/[0.12] bg-[#10183a] text-[#d7dcef]'
      : 'border-slate-200 bg-white text-slate-700'}`}
    >
      <span className={`h-2.5 w-2.5 animate-pulse rounded-full ${isSaaS ? 'bg-[#28ade3]' : 'bg-blue-600'}`} aria-hidden="true" />
      <p className="text-sm font-medium">Signing out…</p>
    </section>
  </main>
);

const ProtectedRoutes = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [sessionState, setSessionState] = useState('checking');
  const [sessionUser, setSessionUser] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const [logoutTransition, setLogoutTransition] = useState(readLogoutTransition);

  useEffect(() => {
    const handleLogoutTransition = (event) => {
      setLogoutTransition(event instanceof CustomEvent ? event.detail : readLogoutTransition());
    };
    window.addEventListener(LOGOUT_TRANSITION_EVENT, handleLogoutTransition);
    return () => window.removeEventListener(LOGOUT_TRANSITION_EVENT, handleLogoutTransition);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('access_token');

    if (!token) {
      dispatch(disconnectSocket());
      setSessionState('unauthenticated');
      return () => { cancelled = true; };
    }

    setSessionState('checking');
    setVerificationMessage('');

    const verifyWebSession = async () => {
      const response = await CommanServices.getMe();
      if (cancelled) return;

      const user = extractAuthUser(response);
      if (!user) {
        dispatch(disconnectSocket());
        const supportSession = localStorage.getItem('support_session');
        const platformBackupRaw = localStorage.getItem('platform_session_backup');
        if (supportSession && platformBackupRaw) {
          try {
            const platformBackup = JSON.parse(platformBackupRaw);
            if (platformBackup?.access_token && platformBackup?.user) {
              localStorage.setItem('access_token', platformBackup.access_token);
              localStorage.setItem('user', JSON.stringify(platformBackup.user));
              if (platformBackup.active_institution) {
                localStorage.setItem('active_institution', platformBackup.active_institution);
              } else {
                localStorage.removeItem('active_institution');
              }
              localStorage.removeItem('support_session');
              localStorage.removeItem('platform_session_backup');
              CommanServices.setToken(platformBackup.access_token);
              window.dispatchEvent(new Event('saas-session-changed'));
              window.history.replaceState(
                {},
                '',
                platformBackup.return_path || '/support-access',
              );
              window.dispatchEvent(new PopStateEvent('popstate'));
              setVerificationAttempt((attempt) => attempt + 1);
              return;
            }
          } catch (_) {
            // Corrupt local recovery data must not replace a server-verified
            // identity; fall through to the normal verification error.
          }
        }
        const impersonating = localStorage.getItem('impersonating');
        const adminToken = localStorage.getItem('admin_token');
        const adminUser = localStorage.getItem('admin_user');
        if (impersonating && adminToken && adminUser) {
          // The short-lived target token may expire before Layout (and its
          // banner) can render. Restore the initiating administrator here so
          // automatic expiration can never trap the browser on an error page.
          localStorage.setItem('access_token', adminToken);
          localStorage.setItem('user', adminUser);
          localStorage.removeItem('impersonating');
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          CommanServices.setToken(adminToken);
          try {
            const sessionId = JSON.parse(impersonating)?.session_id;
            if (sessionId) await SaaSServices.endImpersonationSession(sessionId);
          } catch (_) {
            // The server session is already expired/revoked or unavailable;
            // local administrator recovery must still continue.
          }
          window.dispatchEvent(new Event('impersonation-changed'));
          setVerificationAttempt((attempt) => attempt + 1);
          return;
        }
        setVerificationMessage(response?.message || 'We could not verify this web session.');
        setSessionState('verification_error');
        return;
      }

      if (!hasWebAccess(user)) {
        clearWebSession();
        dispatch(disconnectSocket());
        setSessionUser(null);
        setSessionState('mobile_only');
        return;
      }

      localStorage.setItem('user', JSON.stringify(user));
      setSessionUser(user);
      setSessionState('ready');
      dispatch(connectSocket(token));
    };

    verifyWebSession();
    return () => { cancelled = true; };
  }, [dispatch, verificationAttempt]);

  // Logout clears local identity before React Router finishes replacing the
  // protected route. Keep a transition screen above every verification and
  // permission branch so an intermediate AccessNotice can never be painted.
  if (logoutTransition) {
    return <LogoutTransitionScreen isSaaS={logoutTransition.isSaaS} />;
  }

  // React Router may publish the public URL to this retiring protected tree
  // for one concurrent render before it unmounts it. Never evaluate a public
  // login URL as a protected permission check: doing so caused the brief
  // "Administrator access required" page seen during some logouts.
  if (location.pathname === '/login') {
    return <LogoutTransitionScreen isSaaS={isPlatformSuperadmin(sessionUser)} />;
  }

  if (sessionState === 'checking') {
    return (
      <main className="min-h-screen bg-slate-100 flex items-center justify-center" role="status">
        <p className="text-sm font-medium text-slate-600">Verifying web access…</p>
      </main>
    );
  }

  if (sessionState === 'mobile_only') {
    return (
      <AccessNotice
        title="Use the mobile app"
        message={MOBILE_APP_ONLY_MESSAGE}
        actionTo="/login"
        actionLabel="Back to login"
      />
    );
  }

  if (sessionState === 'verification_error') {
    return (
      <main className="min-h-screen bg-slate-100 px-6 py-12 flex items-center justify-center">
        <section className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-xl" role="alert">
          <h1 className="text-2xl font-bold text-slate-900">Web access could not be verified</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">{verificationMessage}</p>
          <button
            type="button"
            onClick={() => setVerificationAttempt((attempt) => attempt + 1)}
            className="mt-6 min-h-10 rounded-lg bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Try again
          </button>
        </section>
      </main>
    );
  }

  if (sessionState === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  if (!canAccessWebPath(sessionUser, location.pathname, location.search)) {
    const platformAccount = isPlatformSuperadmin(sessionUser);
    return (
      <AccessNotice
        title="Administrator access required"
        message="Your web account does not have permission to open this page."
        actionTo={platformAccount ? "/saas-administration" : "/dashboard"}
        actionLabel={platformAccount ? "Return to SaaS Administration" : "Return to dashboard"}
      />
    );
  }

  const disabledFeature = disabledFeatureForWebPath(sessionUser, location.pathname);
  if (disabledFeature) {
    return (
      <AccessNotice
        title="Feature not included in this license"
        message={`${labelForLicenseFeature(disabledFeature)} is not enabled for your institution. Contact your SaaS administrator if your institution needs access.`}
        actionTo="/dashboard"
        actionLabel="Return to dashboard"
      />
    );
  }

  return children;
};

export default ProtectedRoutes
